import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';

export interface YearRateForDrawer {
  id: string;
  calendar_year: number;
  cost_rate: number;
  sales_rate: number;
  cost_group?: string;
}

interface CostGroupOption {
  id: string;
  name: string;
}

interface CostGroupYearRateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  yearRate?: YearRateForDrawer | null;
  costGroups?: CostGroupOption[];
  /** Year to use when creating a new rate (from parent's year filter). */
  selectedYear?: number | null;
  onSave: (
    payload: { calendar_year: number; cost_rate: number; sales_rate: number },
    id?: string,
    groupId?: string
  ) => undefined | Promise<unknown>;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

const currentYear = new Date().getFullYear();

export const CostGroupYearRateDrawer: React.FC<CostGroupYearRateDrawerProps> = ({
  isOpen,
  onClose,
  yearRate = null,
  costGroups = [],
  selectedYear = null,
  onSave,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const isEdit = !!yearRate?.id && !readOnly;
  const [cost_group_id, setCost_group_id] = useState('');
  const [cost_rate, setCost_rate] = useState(0);
  const [sales_rate, setSales_rate] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCost_group_id(costGroups[0]?.id ?? '');
      setCost_rate(yearRate?.cost_rate ?? 0);
      setSales_rate(yearRate?.sales_rate ?? 0);
      setError(null);
      setSubmitError(null);
    }
  }, [isOpen, yearRate?.id, yearRate?.cost_rate, yearRate?.sales_rate, costGroups]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const yearNum = isEdit
      ? yearRate!.calendar_year
      : selectedYear != null
        ? selectedYear
        : currentYear;
    if (!isEdit && costGroups.length > 0 && !cost_group_id) {
      setError('Cost group is required');
      return;
    }
    setError(null);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const payload = {
        calendar_year: yearNum,
        cost_rate: Number(cost_rate) || 0,
        sales_rate: Number(sales_rate) || 0,
      };
      await Promise.resolve(onSave(payload, yearRate?.id, cost_group_id || undefined));
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View year rate' : isEdit ? 'Edit year rate' : 'Add year rate'}
      description={
        readOnly
          ? 'Year rate details.'
          : isEdit
            ? 'Update cost rate and sales rate.'
            : 'Enter cost rate and sales rate.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update' : 'Add year rate'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        {readOnly ? (
          <>
            <TextFormField
              label="Cost group"
              required
              value={yearRate?.cost_group ?? 'N/A'}
              onChange={() => {}}
              disabled
              copyable
            />
            <TextFormField
              label="Calendar year"
              required
              value={yearRate?.calendar_year != null ? String(yearRate.calendar_year) : 'N/A'}
              onChange={() => {}}
              disabled
              copyable
            />
          </>
        ) : (
          !isEdit &&
          costGroups.length > 0 && (
            <SelectFormField
              label="Cost group"
              required
              value={cost_group_id}
              onChange={(v) => {
                setCost_group_id(v);
                if (error) setError(null);
              }}
              options={costGroups.map((g) => ({ value: g.id, label: g.name }))}
              placeholder="Select group"
              error={error ?? undefined}
            />
          )
        )}
        <TextFormField
          label="Cost rate"
          type={readOnly ? 'text' : 'number'}
          value={String(cost_rate)}
          onChange={(v) => setCost_rate(Number.parseFloat(v) || 0)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Sales rate"
          type={readOnly ? 'text' : 'number'}
          value={String(sales_rate)}
          onChange={(v) => setSales_rate(Number.parseFloat(v) || 0)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
